import type {
  IPatternColorblockPill,
  IPatternColorblockSwatch,
} from 'common/interfaces/print-decor.interface';
import type { IDecoratingMethodSelectionPart } from '../common/interfaces/design.interface';
import {
  isPatternsAndColorblockMethod,
  parseDecorMethodInfo,
  parseDecoratingMethodSelection,
} from './DecoratingMethodUtils';
import {
  getPatternColorblockMergeTreatmentIds,
  isFullGarmentPart,
} from './PrintConfigPatternIdsUtils';
import {
  getPatternLabel,
  getPatternSwatchBackground,
  getPatternSwatchImageUrl,
} from '@/constants/GarmentPatterns';

/** A full garment pattern reads as one choice rather than a part, matching the proof design. */
const FULL_GARMENT_PATTERN_LABEL = 'One Pattern';
const FULL_GARMENT_COLOR_LABEL = 'One Color';

/**
 * Resolves a fabric hex to its colour name. Only needed for proofs saved before the Design Tool
 * started persisting `colorName`; newer proofs carry the name and never consult this.
 */
export type FabricColorNameResolver = (colorHex: string) => string | null | undefined;

interface MergedPart extends IDecoratingMethodSelectionPart {
  /** Preserves the order parts were chosen in so pills render predictably. */
  order: number;
}

const hasText = (value: string | null | undefined): boolean => !!value && value.trim() !== '';

/**
 * A part chosen for both treatments appears under each one — colorblock carries its fabric colour
 * and custom pattern carries its pattern — so merge by part id into a single pill.
 */
const mergePartsByTreatment = (
  selection: Record<string, { parts: IDecoratingMethodSelectionPart[] }>
): MergedPart[] => {
  const byPartId = new Map<number, MergedPart>();

  getPatternColorblockMergeTreatmentIds().forEach((treatment) => {
    const parts = selection[String(treatment)]?.parts;

    if (!Array.isArray(parts)) {
      return;
    }

    parts.forEach((part) => {
      const id = Number(part?.id);

      if (!Number.isFinite(id) || id <= 0) {
        return;
      }

      const existing = byPartId.get(id);

      if (existing) {
        byPartId.set(id, { ...part, ...existing, order: existing.order });

        return;
      }

      byPartId.set(id, { ...part, id, order: byPartId.size });
    });
  });

  return Array.from(byPartId.values()).sort((a, b) => a.order - b.order);
};

const buildPatternSwatch = (part: MergedPart): IPatternColorblockSwatch | null => {
  if (!hasText(part.pattern)) {
    return null;
  }

  const tint = part.patternColor ?? '';

  return {
    kind: 'pattern',
    label: getPatternLabel(part.pattern),
    colorHex: tint,
    background: getPatternSwatchBackground(part.pattern, tint),
    imageUrl: getPatternSwatchImageUrl(part.pattern, part.patternImageUrl),
  };
};

/**
 * The colour a pattern is tinted with, shown as its own solid swatch next to the pattern artwork so
 * the pill reads `Stripes 1, Slate Blue`. Patterns that are not colourable have no tint.
 */
const buildPatternColorSwatch = (part: MergedPart): IPatternColorblockSwatch | null => {
  if (!hasText(part.pattern) || !hasText(part.patternColor)) {
    return null;
  }

  const colorHex = (part.patternColor as string).trim();
  const name = hasText(part.patternColorName) ? (part.patternColorName as string).trim() : null;

  return {
    kind: 'patternColor',
    label: name ?? colorHex,
    colorHex,
    background: colorHex,
    imageUrl: null,
  };
};

const buildColorSwatch = (
  part: MergedPart,
  resolveFabricColorName?: FabricColorNameResolver
): IPatternColorblockSwatch | null => {
  if (!hasText(part.color)) {
    return null;
  }

  const colorHex = (part.color as string).trim();
  const resolved = hasText(part.colorName)
    ? (part.colorName as string).trim()
    : resolveFabricColorName?.(colorHex);

  return {
    kind: 'color',
    label: hasText(resolved) ? (resolved as string) : colorHex,
    colorHex,
    background: colorHex,
    imageUrl: null,
  };
};

const buildPill = (
  part: MergedPart,
  resolveFabricColorName?: FabricColorNameResolver
): IPatternColorblockPill | null => {
  const hasPattern = hasText(part.pattern);
  // Match V4: colourblock-only parts get a fabric swatch; patterned parts keep pattern + tint only.
  const swatches = [
    buildPatternSwatch(part),
    buildPatternColorSwatch(part),
    ...(!hasPattern ? [buildColorSwatch(part, resolveFabricColorName)] : []),
  ].filter((swatch): swatch is IPatternColorblockSwatch => swatch !== null);

  if (!swatches.length) {
    return null;
  }

  const isFullGarment = isFullGarmentPart(part);
  let label = part.name ?? '';
  if (isFullGarment) {
    label = hasPattern ? FULL_GARMENT_PATTERN_LABEL : FULL_GARMENT_COLOR_LABEL;
  }
  const names = swatches.map((swatch) => swatch.label).join(', ');

  return {
    partId: part.id,
    label,
    text: hasText(label) ? `${label}: ${names}` : names,
    isFullGarment,
    swatches,
  };
};

/**
 * Build the proof pills for a "Patterns and/or Colorblock" selection.
 *
 * A single pattern covering the whole garment collapses to one `One Pattern: …` pill; colourblock
 * only uses `One Color: …`. Per-part colourblock-only selections (e.g. `Sleeve: Red`) are shown
 * like V4. Patterned parts still show pattern + tint only.
 *
 * Colour names come from the saved selection. The optional resolver only covers fabric colours on
 * proofs saved before the Design Tool started persisting them, and the hex is the last resort.
 *
 * @param decorMethodInfo - persisted decor rows, parsed or as the raw JSON string
 * @param resolveFabricColorName - maps a fabric hex to its colour name for older proofs
 */
export const buildPatternColorblockPills = (
  decorMethodInfo: unknown,
  resolveFabricColorName?: FabricColorNameResolver
): IPatternColorblockPill[] =>
  parseDecorMethodInfo(decorMethodInfo).reduce<IPatternColorblockPill[]>((pills, row) => {
    if (!isPatternsAndColorblockMethod(row?.decoratingMethod?.id, row?.decoratingMethod?.name)) {
      return pills;
    }

    const selection = parseDecoratingMethodSelection(row?.selection);

    if (selection === null) {
      return pills;
    }

    return pills.concat(
      mergePartsByTreatment(selection)
        .map((part) => buildPill(part, resolveFabricColorName))
        .filter((pill): pill is IPatternColorblockPill => pill !== null)
    );
  }, []);
